"use client";

// WO-GF-001-P1B-S16 — SHARED CALENDAR POPOVER.
//
// Extracted verbatim from the S5 (M5) `DatePickerLayer` calendar so the transport booking widget's
// Depart / Return fields (F94) and the marketplace per-product date fields render the SAME calendar
// instead of growing a second implementation. The only generalisation is the value contract: this
// component speaks `Date`, and each caller owns its own on-screen FORMAT —
//   • marketplace product rows  → "yyyy-mm-dd"        (what the B3.4 cart already reads)
//   • transport booking widget  → "Wed Jul 22, 2026"  (live's react-datepicker display format)
// which is why the format cannot live in here.
//
// §0f (live snowbus.com, read-only, 2026-07-22): both surfaces are real `react-datepicker`s — clicking
// the field opens a month grid popper (`.react-datepicker-popper`), never a map. The open state is
// click-gated and therefore was never captured, so this shell is a functional PH2-RESKIN (same posture
// as ModalShell / the schedule-map), not a pixel match of the legacy react-datepicker.
//
// Renders nothing until a caller mounts it, so at-rest pixel baselines are untouched.

import { useEffect, useRef, useState } from "react";

export type CalendarAnchor = { top: number; left: number };

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export default function CalendarPopover({
  anchor,
  selected,
  minDate,
  onPick,
  onClose,
  ignoreEl,
  label = "Choose date",
}: {
  anchor: CalendarAnchor;
  /** currently-selected day, or null when the field is empty */
  selected: Date | null;
  /** earliest selectable day (defaults to today — a booking date cannot be in the past) */
  minDate?: Date;
  onPick: (d: Date) => void;
  onClose: () => void;
  /** the trigger element, so the opening click does not immediately close the popover */
  ignoreEl?: Element | null;
  label?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const min = startOfDay(minDate || new Date());
  // The month grid opens on the selected value if present, else on the minimum day.
  //
  // ── S36 · card 3 — …BUT NEVER ON A MONTH WITH NOTHING IN IT ──────────────────────────────────────
  // `selected` falls back to whatever the CAPTURED mirror input already holds, and that value is a
  // photocopy of the crawl day — on 2026-08-05 the transport widget's Return field still reads
  // "Sun Jul 19, 2026". So the grid opened on July, every cell was `disabled` (all of it is before
  // today), and the popover was a dialog a person could not answer: measured on the running build,
  // 31 day cells, 0 selectable. F260's fix routes the traveller straight here — pressing Search on the
  // widget's own Roundtrip default opens this calendar to ask for the missing return date — so a
  // month with no selectable day would have replaced one dead end with another, which is the F172
  // dead-control class this estate has already paid for twice.
  //
  // A `selected` day that is BEFORE `min` cannot be picked, so it cannot be what the grid opens on.
  // No-op whenever the selection is real (`selected >= min`), and the popover renders nothing at rest,
  // so no pixel baseline can move.
  const [view, setView] = useState(() => {
    const base = selected && startOfDay(selected) >= min ? selected : min;
    return { year: base.getFullYear(), month: base.getMonth() };
  });

  // F98 — CLOSE BEHAVIOUR: Escape or a click anywhere outside. Same posture as ModalShell and the
  // From/To dropdown, so every overlay in the widget closes the same way.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ref.current && !ref.current.contains(t) && t !== ignoreEl && !ignoreEl?.contains(t)) onClose();
    };
    document.addEventListener("keydown", onKey);
    // defer the outside-click bind a tick so the opening mousedown does not immediately close it.
    const timer = window.setTimeout(() => document.addEventListener("mousedown", onDown), 0);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
      window.clearTimeout(timer);
    };
  }, [onClose, ignoreEl]);

  const first = new Date(view.year, view.month, 1);
  const leadBlanks = first.getDay();
  const daysInMonth = new Date(view.year, view.month + 1, 0).getDate();
  const cells: Array<Date | null> = [];
  for (let i = 0; i < leadBlanks; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(view.year, view.month, d));

  const step = (delta: number) => {
    const m = view.month + delta;
    setView({ year: view.year + Math.floor(m / 12), month: ((m % 12) + 12) % 12 });
  };

  const sameDay = (a: Date, b: Date | null) =>
    !!b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  const navBtn: React.CSSProperties = {
    border: "none",
    background: "none",
    cursor: "pointer",
    fontSize: 18,
    lineHeight: 1,
    padding: "2px 8px",
    color: "#333",
  };

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label={label}
      data-datepicker-popover=""
      style={{
        position: "absolute",
        top: anchor.top + 4,
        left: anchor.left,
        zIndex: 3500,
        background: "#fff",
        borderRadius: 10,
        boxShadow: "0 8px 32px rgba(0,0,0,0.22)",
        border: "1px solid #e3e6ef",
        padding: 12,
        width: 260,
        fontFamily: '"Roboto", system-ui, sans-serif',
        userSelect: "none",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <button type="button" aria-label="Previous month" style={navBtn} onClick={() => step(-1)}>
          ‹
        </button>
        <span style={{ fontSize: 14, fontWeight: 700, color: "#2b2b2b" }}>
          {MONTHS[view.month]} {view.year}
        </span>
        <button type="button" aria-label="Next month" style={navBtn} onClick={() => step(1)}>
          ›
        </button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
        {WEEKDAYS.map((w) => (
          <span key={w} style={{ textAlign: "center", fontSize: 11, fontWeight: 600, color: "#8a8f9a", padding: "2px 0" }}>
            {w}
          </span>
        ))}
        {cells.map((d, i) => {
          if (!d) return <span key={`b${i}`} />;
          const disabled = startOfDay(d) < min;
          const isSel = sameDay(d, selected);
          const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
          return (
            <button
              key={iso}
              type="button"
              data-datepicker-day={iso}
              disabled={disabled}
              onClick={() => onPick(d)}
              style={{
                border: "none",
                borderRadius: 6,
                padding: "6px 0",
                fontSize: 13,
                cursor: disabled ? "default" : "pointer",
                background: isSel ? "#f15a22" : "transparent",
                color: disabled ? "#c7ccd4" : isSel ? "#fff" : "#2b2b2b",
                fontWeight: isSel ? 700 : 400,
              }}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}
